import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useFieldArray, useWatch } from 'react-hook-form';

import { GenericQuestion } from '@components/organisms';

const GenericForm = ({
    control, formState, getValues, questions, setValue, surveyType,
}) => {
    const { fields } = useFieldArray({
        control,
        name: 'answers',
    });
    const watchedAnswers = useWatch({ control, name: 'answers' });

    const questionNumOrderIndexMap = useMemo(() => questions.reduce((acc, q, index) => {
        if (q.num_order)
            acc[q.num_order] = index;

        return acc;
    }, {}), [questions]);

    const isVisible = useCallback((question, answers) => {
        const checkRule = (rule) => {
            if (!rule)
                return true;

            if (rule.type === 'condition' && rule.operator === 'EQUALS') {
                const parentQuestionIndex = questionNumOrderIndexMap[rule.parent_num_order];

                if (parentQuestionIndex !== undefined) {
                    const parentAnswer = answers?.[parentQuestionIndex]?.answer;
                    return rule.values.includes(parentAnswer);
                }
                return false;
            }
            if (rule.type === 'group' && rule.operator === 'AND')
                return rule.conditions.every(checkRule);

            return true;
        };
        return checkRule(question.visibility_rule);
    }, [questionNumOrderIndexMap]);

    useEffect(() => {
        if (!watchedAnswers)
            return;

        questions.forEach((question, idx) => {
            if (!isVisible(question, watchedAnswers)) {
                const currentAnswer = watchedAnswers[idx]?.answer;
                if (currentAnswer !== null && currentAnswer !== undefined)
                    setValue(`answers.${idx}.answer`, null, { shouldValidate: true });
            }
        });
    }, [watchedAnswers, questions, setValue, questionNumOrderIndexMap, isVisible]);

    let fieldIndex = 0;

    return (
        <>
            {fields.map((field, idx) => {
                const question = questions[idx];

                if (!isVisible(question, watchedAnswers))
                    return null;

                const isSubheading = field.field === 'SUBHEADING';

                if (!isSubheading)
                    fieldIndex += 1;

                return (
                    <GenericQuestion
                        control={control}
                        field={field}
                        fieldIndex={fieldIndex}
                        formState={formState}
                        getValues={getValues}
                        idx={idx}
                        key={field.id}
                        question={question}
                        setValue={setValue}
                        surveyType={surveyType}
                    />
                );
            })}
        </>
    );
};

GenericForm.propTypes = {
    control: PropTypes.shape({
        register: PropTypes.func,
    }).isRequired,
    formState: PropTypes.shape({
        errors: PropTypes.shape({
            answers: PropTypes.arrayOf(
                PropTypes.shape({
                    answer: PropTypes.shape({
                        message: PropTypes.string,
                    }),
                }),
            ),
        }),
    }).isRequired,
    getValues: PropTypes.func.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
    setValue: PropTypes.func.isRequired,
    surveyType: PropTypes.string.isRequired,
};

export default GenericForm;
